import { Injectable } from '@angular/core';
import { AppConfig } from '@core_n/app.config';
import { VideoMsgMngrService } from '@core_n/msg/video-mngr.service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { PresetWindowService } from './preset-window.service';
import { Layout, Preset } from './preset.model';
import { PresetWindowState } from './preset/preset.component';

@Injectable({
  providedIn: 'root'
})

export class PresetService {

  private presetsSubject = new BehaviorSubject<Preset[]>([]);
  readonly presets$ = this.presetsSubject.asObservable()  ;

  private presetSubject = new BehaviorSubject<Preset>({ name: '', cameras: [], layoutId: 0});
  get preset$(): Observable<Preset> {
    return this.presetSubject.asObservable();
  }
  
  private loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  private loaded = false;

  private presetLayouts: Layout[] = AppConfig.presets;
  public layoutIdSymbolMap = new Map<number, string>(this.presetLayouts.map(pl => [pl.id, pl.symbol] ));
  
  constructor(private videoApiService :VideoMsgMngrService, private presetWindowService: PresetWindowService) {
  }

  getPresets(){
    if(this.loaded) return this.presets$;

    this.loadingSubject.next(true);

    return this.videoApiService.getPresets().pipe(
      tap(list => {
        this.loaded = true;
        this.presetsSubject.next(list || []);
      }),
      switchMap(() => this.presets$),
      catchError(err => {
        console.error(err);
        return this.presets$;
      }),
      finalize(() => this.loadingSubject.next(false))
      );
  }

  addPreset(preset: Preset){
    this.videoApiService.addPreset(preset).pipe(tap(saved => {
      if(saved){
        const next = [ ...this.presetsSubject.getValue(), saved ];
        this.presetsSubject.next(next);
      }
    }),
    catchError(err =>{
      console.error(err);
      return throwError(err);
    })).subscribe();
  }

  updatePreset(preset: Preset){
    this.videoApiService.updatePreset(preset).pipe(tap(saved => {
      if(saved){
        const next = [ ...this.presetsSubject.getValue().filter(p => p.id != preset.id), saved ];
        this.presetsSubject.next(next);
      }
    }),
    catchError(err =>{
      console.error(err);
      return throwError(err);
    })).subscribe();
  }

  removePreset(pId: number){
    this.videoApiService.removePreset(pId).pipe(tap(removed => {
      if(removed?.id != null){
        const next = this.presetsSubject.getValue().filter(p => p.id != removed.id);
        this.presetsSubject.next(next);
      }
    }),
    catchError(err =>{
      console.error(err);
      return throwError(err);
    })).subscribe();
  }

  showPreset(preset: Preset, state: PresetWindowState) {
    this.presetWindowService.showWindow(state);
    this.presetSubject.next(preset);
  }
}
